import { Fragment, memo, useMemo } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { CardGroup, MetricCard } from "@similarweb/pro-logic-components";
import { Badge } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { percentageSignFilter } from "app/filters/ngFilters";
import type { LocationDevice } from "app/pages/digital-marketing/keywords-campaigns/shared/types";

import type { GscKeyword } from "../../../types";

import { CompactMetricItem } from "./CompactMetricItem";
import { useKeywordMetrics } from "./hooks";
import { ShowKeywordsIcon } from "./ShowKeywordsIcon";
import { CompactContainer, CompactDivider } from "./styles";
import type { MetricCardData } from "./types";

type Props = {
    keywords: GscKeyword[];
    selectedCount: number;
    isLoading?: boolean;
    selectedDevice: LocationDevice;
    compact?: boolean;
};

export const KeywordMetrics = memo(({ keywords, selectedCount, isLoading, selectedDevice, compact }: Props) => {
    const { translate } = useTranslate();
    const metrics = useKeywordMetrics(keywords, selectedCount);
    const hasNoData = keywords.length === 0;

    const cardDataMap: Record<string, MetricCardData> = useMemo(
        () => ({
            trackedKeywords: {
                wide: true,
                title: translate("rankTracker.gsc.metrics.trackedKeywords.title"),
                tooltip: translate("rankTracker.gsc.metrics.trackedKeywords.tooltip"),
                value: hasNoData ? null : metrics.trackedKeywords,
                valueEndContent: hasNoData ? undefined : (
                    <Flex
                        alignItems="center"
                        gap="4px"
                    >
                        {selectedDevice === "BOTH" ? <Badge>x2</Badge> : null}
                        <ShowKeywordsIcon
                            keywords={keywords}
                            selectedCount={selectedCount}
                        />
                    </Flex>
                ),
            },
            impressions: {
                title: translate("rankTracker.gsc.metrics.impressions.title"),
                tooltip: translate("rankTracker.gsc.metrics.impressions.tooltip"),
                value: hasNoData ? null : percentageSignFilter()(metrics.impressionsRatio, 0),
            },
            clicks: {
                title: translate("rankTracker.gsc.metrics.clicks.title"),
                tooltip: translate("rankTracker.gsc.metrics.clicks.tooltip"),
                value: hasNoData ? null : percentageSignFilter()(metrics.clicksRatio, 0),
            },
        }),
        [
            hasNoData,
            keywords,
            metrics.clicksRatio,
            metrics.impressionsRatio,
            metrics.trackedKeywords,
            selectedCount,
            selectedDevice,
            translate,
        ],
    );

    const entries = Object.entries(cardDataMap);

    if (compact) {
        return (
            <CompactContainer data-automation="gsc-keyword-metrics">
                {entries.map(([key, card], i) => (
                    <Fragment key={key}>
                        {i > 0 ? <CompactDivider /> : null}
                        <CompactMetricItem {...card} />
                    </Fragment>
                ))}
            </CompactContainer>
        );
    }

    return (
        <div data-automation="gsc-keyword-metrics">
            <CardGroup
                withDividers
                // Fixed height to keep the cards the same size in the loading state
                $height="80px"
            >
                {entries.map(([key, card]) => (
                    <MetricCard
                        key={key}
                        variant="inline"
                        isLoading={isLoading}
                        title={card.title}
                        tooltip={card.tooltip}
                        value={card.value}
                        valueEndContent={card.valueEndContent}
                    />
                ))}
            </CardGroup>
        </div>
    );
});
